import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MagnifyingGlass, MapPin, Crosshair } from '@phosphor-icons/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ORANGE_ICON = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/**
 * DeliveryMap - Interactive map for selecting delivery location
 * @param {Object} props
 * @param {number} props.latitude - Initial latitude
 * @param {number} props.longitude - Initial longitude
 * @param {number} props.radius - Radius in km (default 5)
 * @param {string} props.address - Current address text
 * @param {boolean} props.readOnly - If true, map is non-interactive (for provider view)
 * @param {function} props.onLocationChange - Callback({latitude, longitude, address, radius})
 */
const DeliveryMap = ({
  latitude = 19.4326,
  longitude = -99.1332,
  radius = 5,
  address = '',
  readOnly = false,
  onLocationChange,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [localAddress, setLocalAddress] = useState(address);

  // Initialize map
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;

    const map = L.map(mapRef.current, {
      center: [latitude, longitude],
      zoom: 13,
      zoomControl: true,
      scrollWheelZoom: !readOnly,
      dragging: !readOnly,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([latitude, longitude], {
      draggable: !readOnly,
      icon: ORANGE_ICON,
    }).addTo(map);

    const circle = L.circle([latitude, longitude], {
      radius: radius * 1000,
      color: '#f97316',
      fillColor: '#f97316',
      fillOpacity: 0.1,
      weight: 2,
      dashArray: '5,5',
    }).addTo(map);

    if (!readOnly) {
      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng();
        circle.setLatLng(pos);
        reverseGeocode(pos.lat, pos.lng);
      });

      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        circle.setLatLng(e.latlng);
        reverseGeocode(e.latlng.lat, e.latlng.lng);
      });
    }

    mapInstanceRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;

    // Fix map sizing after render
    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Update map when lat/lng changes externally
  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current || !circleRef.current) return;
    const latlng = [latitude, longitude];
    markerRef.current.setLatLng(latlng);
    circleRef.current.setLatLng(latlng);
    circleRef.current.setRadius(radius * 1000);
    mapInstanceRef.current.setView(latlng, 13);
  }, [latitude, longitude, radius]);

  // Sync external address changes
  useEffect(() => {
    setLocalAddress(address);
  }, [address]);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
        { headers: { 'Accept-Language': 'es' } }
      );
      const data = await res.json();
      const addr = data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setLocalAddress(addr);
      onLocationChange?.({ latitude: lat, longitude: lng, address: addr, radius });
    } catch {
      const addr = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setLocalAddress(addr);
      onLocationChange?.({ latitude: lat, longitude: lng, address: addr, radius });
    }
  }, [onLocationChange, radius]);

  const handleSearch = async () => {
    const query = searchQuery.trim() || localAddress.trim();
    if (!query) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&countrycodes=mx`,
        { headers: { 'Accept-Language': 'es' } }
      );
      const results = await res.json();
      if (results.length > 0) {
        const { lat, lon, display_name } = results[0];
        const newLat = parseFloat(lat);
        const newLng = parseFloat(lon);
        markerRef.current?.setLatLng([newLat, newLng]);
        circleRef.current?.setLatLng([newLat, newLng]);
        mapInstanceRef.current?.setView([newLat, newLng], 14);
        setLocalAddress(display_name);
        setSearchQuery('');
        onLocationChange?.({ latitude: newLat, longitude: newLng, address: display_name, radius });
      } else {
        // No alert needed - just keep current position
      }
    } catch {
      // Silent fail
    } finally {
      setSearching(false);
    }
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        markerRef.current?.setLatLng([lat, lng]);
        circleRef.current?.setLatLng([lat, lng]);
        mapInstanceRef.current?.setView([lat, lng], 14);
        reverseGeocode(lat, lng);
      },
      () => {},
      { enableHighAccuracy: true }
    );
  };

  return (
    <div className="space-y-2" data-testid="delivery-map-container">
      {/* Search bar (only in edit mode) */}
      {!readOnly && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar dirección en México..."
              className="rounded-none pl-10"
              data-testid="map-search-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleSearch}
            disabled={searching}
            className="rounded-none"
            data-testid="map-search-btn"
          >
            {searching ? '...' : 'Buscar'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleLocateMe}
            className="rounded-none"
            title="Usar mi ubicación"
            data-testid="map-locate-btn"
          >
            <Crosshair className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Map container */}
      <div
        ref={mapRef}
        className="w-full border border-zinc-200 dark:border-zinc-700"
        style={{ height: readOnly ? '220px' : '300px' }}
        data-testid="delivery-map"
      />

      {/* Address display */}
      <div className="flex items-start gap-2 text-sm">
        <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
        <span className="text-zinc-700 dark:text-zinc-300" data-testid="map-address-display">
          {localAddress || 'Haz clic en el mapa o busca una dirección'}
        </span>
      </div>

      {/* Radius info */}
      <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
        <span className="w-3 h-3 rounded-full border-2 border-orange-400 border-dashed inline-block" />
        Radio de entrega: {radius} km
      </div>
    </div>
  );
};

export default DeliveryMap;
